import Vue from 'vue';
import defu from 'defu';

const linkSerializer = Vue.extend({
  props: {
    markDef: {
      type: Object,
      required: true
    }
  },

  render(h) {
    return h('a', {
      attrs: {
        href: this.markDef.href
      }
    }, this.$slots.default);
  }

});
const defaultSerializers = {
  types: {
    span: 'span'
  },
  marks: {
    strong: 'strong',
    em: 'em',
    link: linkSerializer,
    underline: 'u'
  },
  styles: {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    ol: 'ol',
    ul: 'ul',
    normal: 'p',
    blockquote: 'blockquote'
  },
  listItem: 'li',
  list: {
    number: 'ol',
    bullet: 'ul'
  },
  container: 'div'
};

function hasOwnProperty(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

function isPortableTextObject(obj) {
  return !!obj && hasOwnProperty(obj, '_type') && typeof obj._type === 'string';
}

function isListItemBlock(obj) {
  return isPortableTextObject(obj) && hasOwnProperty(obj, 'listItem') && typeof obj.listItem === 'string';
}

function isList(obj) {
  return isPortableTextObject(obj) && obj._type === 'list';
}

function isBlock(obj) {
  return isPortableTextObject(obj) && obj._type === 'block';
}

function isSpan(obj) {
  return isPortableTextObject(obj) && obj._type === 'span';
}

function isMarkDef(obj) {
  return !!obj && hasOwnProperty(obj, '_key') && typeof obj._key === 'string' && hasOwnProperty(obj, '_type') && typeof obj._type === 'string';
}

const createList = (listItemBlock, blocks, serializers) => {
  const listItems = getListItems(listItemBlock, blocks);
  return {
    _type: 'list',
    children: listItems,
    list: listItemBlock.listItem
  };
};

const getListItems = (listItemBlock, blocks, serializers) => {
  const listItems = [];
  let block = listItemBlock;
  const level = listItemBlock.level;
  const listStyleType = listItemBlock.listItem;

  while (block) {
    if (isListItemBlock(block) && block.listItem === listStyleType && block.level === level) {
      const children = [];

      if (block.children && Array.isArray(block.children) && block.children.length) {
        children.push({
          _type: 'block',
          style: block.style,
          children: block.children
        });
      }

      listItems.push({
        _type: 'listItem',
        level: block.level,
        children
      });
    } else if (isListItemBlock(block) && block.level !== undefined && block.level > (level || 1) && listItems.length) {
      const list = createList(block, blocks);
      const lastListItem = listItems[listItems.length - 1];
      lastListItem.children = [...lastListItem.children, list];
    } else {
      blocks.unshift(block);
      break;
    }

    const nextBlock = blocks.shift();

    if (nextBlock) {
      block = nextBlock;
    } else {
      break;
    }
  }

  return listItems;
};

const groupListItems = (blocks, serializers) => {
  // Copy the blocks array so we can modify it
  blocks = blocks.slice();
  const newBlocks = [];

  while (blocks.length) {
    const block = blocks.shift();

    if (isListItemBlock(block)) {
      newBlocks.push(createList(block, blocks));
    } else if (block) {
      newBlocks.push(block);
    }
  }

  return newBlocks;
};

const renderSpan = (h, span, serializers, markDefs = []) => {
  if (!span.marks || span.marks.length === 0) return span.text;
  const marks = span.marks.slice().reverse();
  return marks.reduce((previousVNode, mark) => {
    let el = serializers.marks[mark];
    const markDef = markDefs.find(markDef => markDef._key === mark);

    if (markDef) {
      el || (el = serializers.marks[markDef._type]);
    }

    return el ? h(el, {
      props: {
        block: span,
        markDef
      }
    }, [previousVNode]) : previousVNode;
  }, span.text);
};

const renderList = (h, list, serializers) => {
  let children = [];

  if (list.children) {
    children = list.children.map(listItem => renderListItem(h, listItem, serializers));
  }

  return h(list.list === 'number' ? serializers.list.number : serializers.list.bullet, {
    props: {
      block: list
    }
  }, children);
};

const renderListItem = (h, listItem, serializers) => {
  return h(serializers.listItem, {
    props: {
      block: listItem
    }
  }, renderChildren(h, listItem, serializers));
};

const renderChildren = (h, parent, serializers) => {
  let children = [];

  if (parent.children) {
    children = parent.children.map(child => {
      if (isSpan(child)) {
        const markDefs = [];

        if (parent.markDefs && Array.isArray(parent.markDefs)) {
          parent.markDefs.forEach(markDef => {
            if (isMarkDef(markDef)) {
              markDefs.push(markDef);
            }
          });
        }

        return renderSpan(h, child, serializers, markDefs);
      } else if (isList(child)) {
        return renderList(h, child, serializers);
      } else if (isBlock(child)) {
        return renderBlock(h, child, serializers);
      } else {
        return renderCustomType(h, child, serializers);
      }
    });
  }

  return children;
};

const renderBlock = (h, block, serializers) => {
  return h(serializers.styles[block.style || 'normal'], {
    props: {
      block
    }
  }, renderChildren(h, block, serializers));
};

const renderCustomType = (h, obj, serializers) => {
  const serializer = serializers.types[obj._type];
  if (serializer) return h(serializer, {
    props: {
      block: obj
    }
  }, renderChildren(h, obj, serializers));
  return h(serializers.styles.normal, {}, renderChildren(h, obj, serializers));
};

var VuePortableText = Vue.extend({
  name: 'VuePortableText',
  props: {
    as: {
      type: String,
      default: undefined
    },
    blocks: {
      type: Array,
      default: () => []
    },
    serializers: {
      type: Object,
      default: () => ({})
    }
  },

  render(h) {
    const serializers = defu(this.serializers || {}, defaultSerializers);
    const blocks = groupListItems(this.blocks);
    const children = blocks.map(block => {
      if (isBlock(block)) {
        return renderBlock(h, block, serializers);
      } else if (isList(block)) {
        return renderList(h, block, serializers);
      } else {
        return renderCustomType(h, block, serializers);
      }
    });
    return h(this.as || serializers.container, {}, children);
  }

});

export { VuePortableText as default, defaultSerializers };
